import { useState, useEffect, useCallback, useMemo } from 'react';
import CartHandler from '../CartHandler';

const STORAGE_KEY = 'gatsbyWooCart';

const useLocalCart = () => {
  const canUseStorage = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      const testKey = '__storage_test__';
      window.localStorage.setItem(testKey, testKey);
      window.localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      console.warn('Local storage is unavailable, falling back to in-memory cart.', error);
      return false;
    }
  }, []);

  const [cartData, setCartData] = useState(() => {
    try {
      if (!canUseStorage) return [];
      const storedCart = window.localStorage.getItem(STORAGE_KEY);
      return storedCart ? JSON.parse(storedCart) : [];
    } catch (error) {
      console.error('Error parsing cart data from localStorage:', error);
      return [];
    }
  });

  const [cartUser, setCartUser] = useState(null);
  const [coupons, setCoupons] = useState([]);

  const cartHandler = useMemo(() => new CartHandler(cartData, cartUser), [cartData, cartUser]);

  useEffect(() => {
    try {
      if (!canUseStorage) return;
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cartData));
    } catch (error) {
      console.error('Error saving cart data to localStorage:', error);
    }
  }, [cartData, canUseStorage]);

  const attachCustomer = useCallback((userData) => {
    setCartUser(userData);
  }, []);

  const addToCart = useCallback((product) => {
    cartHandler.addItem(product);
    setCartData(cartHandler.cart);
    setCoupons(cartHandler.coupons)
  }, [cartData]);

  const removeFromCart = useCallback((productId) => {
    cartHandler.removeItem(productId);
    setCartData(cartHandler.cart);
    setCoupons(cartHandler.coupons)
  }, [cartData]);

  const updateCartItem = useCallback((productId, quantity) => {
    cartHandler.updateItemQuantity(productId, quantity);
    setCartData(cartHandler.cart);
    setCoupons(cartHandler.coupons)
  }, [cartData]);

  const emptyCart = useCallback(() => {
    cartHandler.clearCart();
    setCartData([]);
    setCoupons([]);
    try {
      if (!canUseStorage) return;
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error removing cart data from localStorage:', error);
    }
  }, [cartData, canUseStorage]);

  const applyCoupon = useCallback((couponData) => {
    cartHandler.applyCoupon(couponData);
    setCoupons(cartHandler.coupons);
  }, [cartData]);

  const removeCoupon = useCallback((code) => {
    cartHandler.removeCoupon(code);
    setCoupons(cartHandler.coupons);
  }, [cartData]);

  const checkout = useCallback(async (userData) => {
    return cartHandler.checkout(userData);
  }, [cartHandler]);

  return {
    cartData,
    customer: cartUser,
    coupons,
    addToCart,
    removeFromCart,
    updateCartItem,
    emptyCart,
    attachCustomer,
    applyCoupon,
    removeCoupon,
    checkout,
    getGrossCartTotal: useCallback(() => cartHandler.getGrossTotal(), [cartData]),
    getNetCartTotal: useCallback(() => cartHandler.getNetTotal(), [cartData]),
    getTotalItems: useCallback(() => cartHandler.getTotalItems(), [cartData]),
    isInCart: useCallback((productId) => cartHandler.isInCart(productId), [cartData]),
    getItemQuantity: useCallback((productId) => cartHandler.getItemQuantity(productId), [cartData]),
    getSubtotal: useCallback(() => cartHandler.getSubtotal(), [cartData]),
    getTotalDiscount: useCallback(() => cartHandler.getTotalDiscount(), [cartData]),
    getTaxAmount: useCallback(() => cartHandler.getTaxAmount(), [cartData]),
    getShippingCost: useCallback(() => cartHandler.getShippingCost(), [cartData])
  };
};

export default useLocalCart;
